import { Part, FlangeId, Flange } from './types';
import { nearestGauge, MATERIALS } from './materials';
import { findMiterCandidates, findAnyCandidate } from './miterPairs';

export interface DesignIssue {
  flangeId: FlangeId;
  severity: 'warn' | 'error';
  code: string;
  message: string;
}

// Distance from a hole or feature to the nearest bend tangent line.
// Industry rule of thumb (matches SCS "½ die width"): 2.5*T + R is generally safe.
export function minFeatureDistance(thickness: number, bendRadius: number) {
  return 2.5 * thickness + bendRadius;
}

export function bendReliefDimensions(thickness: number, bendRadius: number) {
  // SendCutSend rule: depth = T + R + 0.5mm, width = max(T/2, 1.0mm).
  return {
    depth: thickness + bendRadius + 0.5,
    width: Math.max(thickness / 2, 1.0),
  };
}

export function validate(part: Part): DesignIssue[] {
  const issues: DesignIssue[] = [];
  const mat = MATERIALS[part.material];
  const gauge = nearestGauge(part.material, part.thickness);
  const miterCandidates = findMiterCandidates(part);

  // Top-level: thickness range
  const thickRange = [mat.gauges[0].thickness, mat.gauges[mat.gauges.length - 1].thickness] as const;
  if (part.thickness < thickRange[0] - 0.05 || part.thickness > thickRange[1] + 0.05) {
    issues.push({
      flangeId: part.rootId,
      severity: 'warn',
      code: 'thickness_range',
      message: `Thickness ${part.thickness} mm is outside typical ${mat.label} range (${thickRange[0]}–${thickRange[1]} mm).`,
    });
  }

  for (const id of Object.keys(part.flanges)) {
    const f = part.flanges[id];

    // Flange dimensions
    if (f.length < gauge.minFlangeLength) {
      issues.push({
        flangeId: id,
        severity: 'warn',
        code: 'flange_too_short',
        message: `${f.name}: length ${f.length.toFixed(1)} mm is below minimum (${gauge.minFlangeLength.toFixed(1)} mm) for ${part.thickness} mm ${mat.label}.`,
      });
    }
    if (f.width < gauge.minFlangeLength) {
      issues.push({
        flangeId: id,
        severity: 'warn',
        code: 'flange_width_short',
        message: `${f.name}: width ${f.width.toFixed(1)} mm is below recommended ${gauge.minFlangeLength.toFixed(1)} mm.`,
      });
    }

    if (f.attachment) {
      const a = f.attachment;
      const absAngle = Math.abs(a.bendAngle);

      if (absAngle < mat.minBendAngle) {
        issues.push({
          flangeId: id,
          severity: 'warn',
          code: 'angle_too_small',
          message: `${f.name}: bend angle ${a.bendAngle}° is below ${mat.minBendAngle}° (springback dominates).`,
        });
      }
      if (absAngle > mat.maxBendAngle) {
        issues.push({
          flangeId: id,
          severity: 'error',
          code: 'angle_too_large',
          message: `${f.name}: bend angle ${a.bendAngle}° exceeds ${mat.maxBendAngle}° (acute angle limit).`,
        });
      }

      const minR = part.thickness; // R ≥ T as a hard floor; many shops require R ≥ T
      if (a.bendRadius < minR - 1e-6) {
        issues.push({
          flangeId: id,
          severity: 'warn',
          code: 'radius_too_small',
          message: `${f.name}: bend radius ${a.bendRadius.toFixed(2)} mm is below 1× thickness (${part.thickness} mm). Risk of cracking.`,
        });
      }

      // Validate offset/width within parent edge
      const parent = part.flanges[a.parentId];
      if (parent) {
        const edgeLen = a.edge === '+x' || a.edge === '-x' ? parent.width : parent.length;
        if (a.offset < 0 || a.offset + f.width > edgeLen + 1e-6) {
          issues.push({
            flangeId: id,
            severity: 'error',
            code: 'overflow_edge',
            message: `${f.name}: offset+width (${a.offset}+${f.width}=${(a.offset + f.width).toFixed(1)}) exceeds parent edge length ${edgeLen.toFixed(1)} mm.`,
          });
        }

        // Channel ratio: if the parent is short relative to a tall child flange, U-channel will collide
        // Rule (SCS): base ≥ 2 × flange height for sheet metal
        // Channel ratio warning only makes sense for U-channels — i.e. walls
        // bent up from the central base panel. Caps (grandchildren bending
        // inward off the top of a wall) have a different topology and shouldn't
        // be flagged: it's normal for a cap to extend further than half the
        // wall it sits on.
        const parentIsRoot = a.parentId === part.rootId;
        const baseDim = a.edge === '+x' || a.edge === '-x' ? parent.length : parent.width;
        if (parentIsRoot && Math.abs(a.bendAngle - 90) < 5 && f.length > baseDim / 2) {
          issues.push({
            flangeId: id,
            severity: 'warn',
            code: 'channel_ratio',
            message: `${f.name}: flange ${f.length.toFixed(0)} mm > base/2 (${(baseDim / 2).toFixed(0)} mm). For U-channel, base should be ≥ 2× flange height.`,
          });
        }
      }

      // Miter validation: surface why a miter is/isn't taking effect.
      if (a.miterCorner) {
        const cand = findAnyCandidate(miterCandidates, id);
        const buddy: Flange | null = cand ? part.flanges[cand.id] : null;
        if (!buddy) {
          issues.push({
            flangeId: id,
            severity: 'warn',
            code: 'miter_no_buddy',
            message: `${f.name}: Miter is enabled but no perpendicular flange meets at a shared 3D corner. Cut won't apply.`,
          });
        } else if (!buddy.attachment?.miterCorner) {
          issues.push({
            flangeId: id,
            severity: 'warn',
            code: 'miter_one_sided',
            message: `${f.name}: Miter only takes effect when "${buddy.name}" also has Miter enabled.`,
          });
        } else if (f.length > f.width + 1e-3) {
          issues.push({
            flangeId: id,
            severity: 'warn',
            code: 'miter_length_too_large',
            message: `${f.name}: Miter requires length (${f.length.toFixed(1)} mm) ≤ width (${f.width.toFixed(1)} mm); cut skipped.`,
          });
        }
      }
    }
  }

  // Holes: bounds, edge clearance, bend-tangent clearance, hole-to-hole.
  for (const h of part.holes) {
    const f = part.flanges[h.flangeId];
    if (!f) continue;
    const r = h.diameter / 2;
    const len = Math.max(h.length ?? h.diameter, h.diameter);
    // Slot rotation in local frame (radians); we approximate the bounding box
    // for clearance checks regardless of rotation by using the slot's longest extent.
    const halfStraight = (len - h.diameter) / 2;
    const rot = ((h.rotation ?? 0) * Math.PI) / 180;
    const cs = Math.cos(rot), sn = Math.sin(rot);
    // Slot extents in local: ±halfStraight along (cs,sn), ±r perpendicular. Bbox half-extents:
    const bxHalf = Math.abs(halfStraight * cs) + Math.abs(r * sn) + (h.kind === 'round' ? 0 : 0);
    const byHalf = Math.abs(halfStraight * sn) + Math.abs(r * cs);
    const halfX = h.kind === 'round' ? r : bxHalf + r * Math.abs(cs); // approximation
    const halfY = h.kind === 'round' ? r : byHalf + r * Math.abs(sn);
    void halfX; void halfY;
    // Simpler: use a circumscribed-circle radius for clearance checks.
    const circR = h.kind === 'round' ? r : len / 2;

    if (h.x - r < -1e-3 || h.x + r > f.length + 1e-3 || h.y - r < -1e-3 || h.y + r > f.width + 1e-3) {
      issues.push({
        flangeId: h.flangeId,
        severity: 'error',
        code: 'hole_out_of_flange',
        message: `Hole at (${h.x.toFixed(1)}, ${h.y.toFixed(1)}) on ${f.name} extends past the flange boundary.`,
      });
    }

    const minEdge = part.thickness * 2; // SCS rule: ≥ 2T from any non-bend edge
    const distLeft = h.x - circR;
    const distRight = f.length - (h.x + circR);
    const distBot = h.y - circR;
    const distTop = f.width - (h.y + circR);
    const edgeClear = Math.min(distLeft, distRight, distBot, distTop);
    if (edgeClear >= 0 && edgeClear < minEdge - 1e-3) {
      issues.push({
        flangeId: h.flangeId,
        severity: 'warn',
        code: 'hole_too_close_to_edge',
        message: `Hole on ${f.name}: ${edgeClear.toFixed(1)} mm from edge is below 2× thickness (${minEdge.toFixed(1)} mm).`,
      });
    }

    // Bend tangent clearance — for each bend touching this flange (its own attachment
    // makes x=0 a tangent; each child of this flange creates one along the appropriate edge)
    const bendLines: Array<{ axis: 'x' | 'y'; level: number; rangeLo: number; rangeHi: number; bendR: number }> = [];
    if (f.attachment) bendLines.push({ axis: 'x', level: 0, rangeLo: 0, rangeHi: f.width, bendR: f.attachment.bendRadius });
    for (const cid of f.children) {
      const c = part.flanges[cid];
      if (!c?.attachment) continue;
      const O = c.attachment.offset, Wc = c.width;
      const bR = c.attachment.bendRadius;
      switch (c.attachment.edge) {
        case '+x': bendLines.push({ axis: 'x', level: f.length, rangeLo: O, rangeHi: O + Wc, bendR: bR }); break;
        case '-x': bendLines.push({ axis: 'x', level: 0, rangeLo: O, rangeHi: O + Wc, bendR: bR }); break;
        case '+y': bendLines.push({ axis: 'y', level: f.width, rangeLo: O, rangeHi: O + Wc, bendR: bR }); break;
        case '-y': bendLines.push({ axis: 'y', level: 0, rangeLo: O, rangeHi: O + Wc, bendR: bR }); break;
      }
    }
    for (const b of bendLines) {
      const minDist = minFeatureDistance(part.thickness, b.bendR);
      // Distance from hole center to the bend line, only if the hole is within the bend line's range
      const along = b.axis === 'x' ? h.y : h.x;
      if (along < b.rangeLo - circR || along > b.rangeHi + circR) continue;
      const perp = b.axis === 'x' ? Math.abs(h.x - b.level) : Math.abs(h.y - b.level);
      const clear = perp - circR;
      if (clear < minDist - 1e-3) {
        issues.push({
          flangeId: h.flangeId,
          severity: 'warn',
          code: 'hole_too_close_to_bend',
          message: `Hole on ${f.name}: ${clear.toFixed(1)} mm from bend tangent is below 2.5T+R (${minDist.toFixed(1)} mm); risk of distortion.`,
        });
      }
    }
  }

  // Multiple children on same parent edge → suggest reliefs/gap
  for (const id of Object.keys(part.flanges)) {
    const f = part.flanges[id];
    const byEdge = new Map<string, FlangeId[]>();
    for (const cid of f.children) {
      const c = part.flanges[cid];
      if (c?.attachment) {
        const k = c.attachment.edge;
        if (!byEdge.has(k)) byEdge.set(k, []);
        byEdge.get(k)!.push(cid);
      }
    }
    for (const [, ids] of byEdge) {
      if (ids.length > 1) {
        // sort by offset, check gaps
        const sorted = ids
          .map((i) => ({ id: i, a: part.flanges[i].attachment! }))
          .sort((x, y) => x.a.offset - y.a.offset);
        for (let i = 1; i < sorted.length; i++) {
          const prev = sorted[i - 1];
          const cur = sorted[i];
          const prevEnd = prev.a.offset + part.flanges[prev.id].width;
          const gap = cur.a.offset - prevEnd;
          if (gap < 0.4) {
            issues.push({
              flangeId: cur.id,
              severity: 'warn',
              code: 'corner_gap',
              message: `Flange gap ${gap.toFixed(2)} mm to "${part.flanges[prev.id].name}" is below 0.4 mm minimum (corner relief).`,
            });
          }
        }
      }
    }
  }

  return issues;
}
